import SearchIconBtn from '../../assets/icons/SearchIconBtn'

import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { UserSetupForm } from '.'

import { z } from 'zod'
import {
  deleteUser,
  downloadUserExcel,
  getUsers,
  saveUser,
} from '../../api/adminUser.js'
import DownloadIcon from '../../assets/icons/DownloadIcon'
import { columns, searchFields } from '../../constants/adminUser.js'
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { DEFAULT_PAGE, getValidCriteria ,sanitizeForLog} from '../../constants/index.js'
import { useSnackbar } from '../../contexts/SnackbarProvider'
import { CommonModal, CommonTable, ConfirmationDialog, Search } from '../common'
import { saveBlobAsFile } from "../../api/apiUtils.js"

const userIdRegex = /^[a-zA-Z0-9._-]+$/

const newUserSchema = z.object({
  userId: z
    .string()
    .min(1, 'User ID is required')
    .max(50, 'User ID must be at most 50 characters'),
  userName: z
    .string()
    .min(1, 'User Name is required')
    .max(200, 'User Name must be at most 200 characters'),
  email: z
    .string()
    .min(1, 'Email ID is required')
    .max(200, 'Email ID must be at most 200 characters')
    .email('Please enter a valid email address'),
  businessRole: z
    .string()
    .min(1, 'At least one Business Role is required'),
  isLocked: z.boolean(),
  isActive: z.boolean(),
  ssoUser: z.boolean(),
})

const UserSetup = ({ roleConfig }) => {
  // State management
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState('new')
  const [editingUser, setEditingUser] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [startDownloading, setStartDownloading] = useState(false)
  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  // Form state
  const [newUserForm, setNewUserForm] = useState({
    editUserId: '',
    userId: '',
    userName: '',
    email: '',
    businessRole: '',
    isLocked: false,
    isActive: true,
    ssoUser: false,
  })
  const [formErrors, setFormErrors] = useState({})
  const { showSnackbar } = useSnackbar()

  // Load users with pagination and search
  const loadUsers = useCallback(
    async (page = 1, pageSize = itemsPerPage, searchList = []) => {
      try {
        setLoading(true)

        const payload = {
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize: pageSize,
            sortField: 'lastModifiedDate',
            sortOrder: 'DESC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: searchList,
        }

        const response = await getUsers(payload)

        if (response.success && response.data) {
          setUsers(response.data.content)
          setTotalItems(response.data.totalElements)
          setCurrentPage(page)
        } else {
          setUsers([])
        }
      } catch (error) {
        console.error('Error loading users:')
        setUsers([])
        setTotalItems(0)
      } finally {
        setLoading(false)
      }
    },
    [itemsPerPage]
  )

  // Load users on component mount
  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  // Pagination handlers
  const handlePageChange = page => {
    loadUsers(page, itemsPerPage, getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadUsers(1, newItemsPerPage, getValidCriteria(searchCriteria))
  }

  // Download handler
  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info');
      return;
    }

    setStartDownloading(true);
    try {
      const payload = {
        pageFilter: {
          currentPage,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'username',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
      };

      const blob = await downloadUserExcel(payload);

      // ✅ Safe, Veracode-approved helper (centralized XSS mitigation)
      saveBlobAsFile(blob, 'users.xlsx');
    } catch (error) {
      console.error('Failed to download User Excel:', sanitizeForLog(error));
    } finally {
      setStartDownloading(false);
    }
  };

  // Modal handlers
  const handleAddNewUser = () => {
    setModalType('new')
    setEditingUser(null)
    setNewUserForm({
      userId: '',
      userName: '',
      email: '',
      businessRole: '',
      isLocked: false,
      isActive: true,
      ssoUser: false,
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const handleEdit = user => {
    setModalType('edit')
    setEditingUser(user)
    setNewUserForm({
      editUserId: user.userId,
      userId: user.username,
      userName: user.name,
      email: user.emailId,
      businessRole: user.role,
      isLocked: user.isLocked,
      isActive: user.isActive,
      ssoUser: user.isSSOUser,
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const handleDelete = async (user) => {
    const payload = {
      userId: user.userId,
      username: user.username
    };
    try {
      const response = await deleteUser(payload, false) // Check only, do not delete yet;

      if (response && response.success) {
        // If data is false, show confirmation dialog (user has assigned entities)
        if (response.data.data === false) {
          setUserToDelete(user)
          setDeleteDialogOpen(true)
        }
        // If data is true, delete directly without confirmation
        else if (response.data.data === true) {
          await performDelete(user)
        } else {
          showSnackbar('Unexpected response from server', 'error')
        }
      } else {
        showSnackbar('Failed to check user deletion status', 'error')
      }
    } catch (error) {
      showSnackbar(error.message || 'Error checking user deletion status', 'error')
      console.error('Error in handleDelete:')
    }
  }

  // To perform the actual deletion
  const performDelete = async (user) => {
    const payload = {
      userId: user.userId,
      username: user.username
    }
    try {
      const response = await deleteUser(payload, true) // Proceed to delete
      if (response && response.success) {
        showSnackbar('User deleted successfully!', 'success')
      } else {
        showSnackbar('Failed to delete user', 'error')
      }
      // Refresh the user list
      loadUsers(currentPage, itemsPerPage, getValidCriteria(searchCriteria))
    } catch (error) {
      showSnackbar(error.message || 'Error deleting user', 'error')
      console.error('Error in performDelete:')
    }
  }

  const confirmDelete = async () => {
    if (!userToDelete) return;

    // Perform the actual deletion
    await performDelete(userToDelete)

    // Close dialog and clear state
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const handleCancel = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  // Form handlers
  const handleNewUserInputChange = (field, value) => {
    setNewUserForm(prev => {
      const updatedForm = { ...prev, [field]: value }
      try {
        newUserSchema.pick({ [field]: true }).parse({ [field]: value })
        setFormErrors(prevErrors => ({ ...prevErrors, [field]: '' }))
      } catch (error) {
        if (error instanceof z.ZodError) {
          setFormErrors(prevErrors => ({
            ...prevErrors,
            [field]: error.issues[0].message,
          }))
        }
      }
      return updatedForm
    })
  }
  const handleFormSubmit = async e => {
    e.preventDefault()
    try {
      newUserSchema.parse({
        ...newUserForm,
        isLocked: Boolean(newUserForm.isLocked),
        isActive: Boolean(newUserForm.isActive),
        ssoUser: Boolean(newUserForm.ssoUser),
      })

      setFormLoading(true)
      const payload = {
        editUserId: newUserForm.editUserId,
        username: newUserForm.userId,
        name: newUserForm.userName,
        emailId: newUserForm.email,
        isAdmin: false,
        isActive: newUserForm.isActive,
        isLocked: newUserForm.isLocked,
        isSSOUser: newUserForm.ssoUser,
        roleTypes: newUserForm.businessRole,
      }

      const response = await saveUser(payload)

      if (response.success) {
        showSnackbar(response.data || 'User saved successfully!', 'success')
        await loadUsers(currentPage, itemsPerPage, [])
        setFormLoading(false)
        setModalOpen(false)
        setEditingUser(null)
      } else {
        const errorMsg =
          response.errors && response.errors.length > 0
            ? response.errors[0].message
            : 'Failed to save user'

        setFormErrors({ general: errorMsg })
        throw new Error(errorMsg)
      }
    } catch (error) {
      setFormLoading(false)

      if (error instanceof z.ZodError) {
        const formErrors = {}
        error.issues.forEach(err => {
          formErrors[err.path[0]] = err.message
        })
        setFormErrors(formErrors)
      } else {
        const errMsg = error.message || 'An unexpected error occurred'
        setFormErrors({ general: errMsg })
      }
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingUser(null)
    setFormErrors({})
  }

  // search
  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadUsers(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadUsers(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  return (
    <Box>
      {/* Sub Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '32px',
          py: '12px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            color: 'var(--forbrand-secondary)',
            fontWeight: 500
          }}
        >
          User Setup
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
            <span className="disabled:cursor-not-allowed">
              <IconButton
                onClick={handleDownload}
                disabled={loading || startDownloading}

              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
            <IconButton
              onClick={handleSearch}

            >
              <SearchIconBtn />
            </IconButton>
          </Tooltip>

          {roleConfig.canEdit && (
            <>
              <Typography
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--forbrand-color)',
                  marginRight: '6px',
                }}
              />

              <Button
                variant="contained"
                onClick={handleAddNewUser}
                sx={{
                  backgroundColor: 'var( --forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                Add User
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Table with Pagination */}
      <Box>
        <CommonTable
          columns={columns}
          data={users}
          loading={loading}
          onEdit={handleEdit}
          disabledEdit={true}
          currentTable={"tableUsingIsActive"}
          onDelete={handleDelete}
          emptyMessage="No users found"
          showFooter={true}
          totalItems={totalItems}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          showTotalItems={true}
          showPageNumbers={true}
          showActions={roleConfig.canEdit}
        />
      </Box>

      {/* Add/Edit User Modal */}
      <CommonModal
        open={modalOpen}
        onClose={handleModalClose}
        title={''}
        showCloseButton={modalType !== 'new'}
        maxWidth={'sm'}
        sx={{
          '& .MuiDialogContent-root': {
            padding: 0,
          },
          '& .MuiDialogTitle-root': {
            display: 'none',
          },
        }}
      >
        <UserSetupForm
          formData={newUserForm}
          formErrors={formErrors}
          editingUser={editingUser}
          formLoading={formLoading}
          onInputChange={handleNewUserInputChange}
          onSubmit={handleFormSubmit}
          onClose={handleModalClose}
        />
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this user?"
        message="This user has already assigned Entities. Necessary changes w.r.t. assignment of Entities and TANs is to be done. Are you sure you want to delete this user?"
        confirmText="Proceed"
        itemType=""
        type="delete"
        showCancelButton={true}
        onCancel={handleCancel}
      />

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />
    </Box>
  )
}

export default UserSetup
